<script setup lang="ts">
import { useClaimInvoices } from '@/composables/asicw/claim-invoices'
import { useClaims } from '@/composables/asicw/claims'
import { dateFormatter } from '@/helpers/ep/formatters'
import type { ICreateClaimInvoiceForm } from '@/types/asicw/invoices'
import { ClaimStatus, type IClaimFilters } from '@/types/asicw/claims'
import { formatCurrency } from '@/util/formatters'
import type { FormInstance } from 'element-plus'
import { reactive, ref } from 'vue'

const claimFilters = reactive<IClaimFilters>({
  page: 1,
  per_page: 10000, // Hardcoded. This is enough for now.
  customer_id: undefined,
  status: ClaimStatus.Completed,
  has_costs: true,
  fragment: undefined,
})

// Composables
const { createClaimInvoiceMutation } = useClaimInvoices()
const { getClaimsQuery } = useClaims()
const { mutateAsync: handleCreateInvoices, isPending: isCreatePending } = createClaimInvoiceMutation
const { data: claimsData, isFetching } = getClaimsQuery(claimFilters)

const emit = defineEmits<{
  (e: 'closed'): void
}>()

const dialogVisible = defineModel<boolean>()
const selectedPeriod = ref<string>()

const formRef = ref<FormInstance>()
const form = reactive<ICreateClaimInvoiceForm>({
  year: 0,
  month: 0,
  claim_ids: [],
})

const rules = {
  period: [
    {
      validator: (_rule: any, _value: any, callback: any) => {
        if (form.year === 0 || form.month === 0) {
          callback(new Error('Period is required'))
        } else {
          callback()
        }
      },
      trigger: 'change',
    },
  ],
  claim_ids: [{ type: 'array' as const, min: 1, message: 'At least one claim must be selected' }],
}

const onSubmit = async () => {
  // Validate form before submission
  await formRef.value?.validate(async (valid: boolean) => {
    if (!valid) {
      return
    }

    try {
      await handleCreateInvoices(form)
      dialogVisible.value = false
    } catch {
      // Error is handled by mutation error handler
    }
  })
}

const onClosed = () => {
  formRef.value?.resetFields()
  emit('closed')
}

const handleClaimSelectionChange = (selectedClaims: any[]) => {
  form.claim_ids = selectedClaims.map((claim) => claim.id)
}

const handlePeriodChange = (value: string | null) => {
  if (!value) {
    form.year = 0
    form.month = 0
    return
  }

  // Parse the YYYY-MM-DD string directly to avoid timezone issues
  const [year, month] = value.split('-').map(Number)
  form.year = year
  form.month = month
}
</script>

<template>
  <el-dialog v-model="dialogVisible" title="Create Claim Invoices" width="1000px" align-center @closed="onClosed">
    <el-form ref="formRef" :model="form" :rules="rules" label-position="left" class="space-y-5">
      <el-form-item label="Period" prop="period">
        <el-date-picker
          v-model="selectedPeriod"
          type="month"
          placeholder="Select month"
          format="MMMM YYYY"
          value-format="YYYY-MM-DD"
          clearable
          @change="handlePeriodChange"
        />
      </el-form-item>

      <el-form-item label="Claims" prop="claim_ids">
        <div>
          <el-table
            v-loading="isFetching"
            :data="claimsData?.data"
            height="400"
            no-data-text="No claims found"
            @selection-change="handleClaimSelectionChange"
          >
            <el-table-column type="selection" width="55" />
            <el-table-column prop="policy_number" label="Policy #" :min-width="200" />
            <el-table-column prop="number" label="Claim #" :min-width="200" />
            <el-table-column prop="miner.sn" label="Miner SN" :min-width="200" />
            <el-table-column prop="date_of_loss" label="Date of Loss" :min-width="120" :formatter="dateFormatter" />
            <el-table-column label="Costs" :min-width="120">
              <template #default="scope">
                {{ scope.row.costs ? formatCurrency(scope.row.costs.total) : '-' }}
              </template>
            </el-table-column>
          </el-table>
          <div v-if="form.claim_ids.length > 0" class="mt-2 flex justify-end">
            <el-tag type="info" size="small"> {{ form.claim_ids.length }} claim(s) selected </el-tag>
          </div>
        </div>
      </el-form-item>
    </el-form>

    <!-- Footer actions -->
    <template #footer>
      <div class="flex justify-end gap-3">
        <el-button :disabled="isCreatePending" @click="dialogVisible = false">Cancel</el-button>
        <el-button type="primary" :loading="isCreatePending" @click="onSubmit"> Create </el-button>
      </div>
    </template>
  </el-dialog>
</template>
